import { message } from 'antd';
import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';

import { environments } from '../constants';
import { useAuthStore } from '../store';
import { parseError } from '../utils/parseError';
import { useRespondAuthChallengeArgs } from '../models';
import { get_all_user_data_v2 } from '../services/AuthActions';
import { RESPONDTOAUTHCHALLENGESMS } from '../graphql/Mutations';
import { UserException, allowedUserType, populateSessionStorage } from '../utils/helpers';
import { ESessionStorageKeys } from '../../../types';
import mixpanelService from '../services/mixpanel';
import { EMixPanelAuthTypes, EMixPanelMetadataKeys } from '../../types';

export const useRespondAuthChallenge = ({
  isAfterSignUp = false,
  variables,
  onCompletedCallback,
}: useRespondAuthChallengeArgs) => {
  const { session, smsMfaCode, username } = variables;
  const appType = useAuthStore((s) => s.currentAppState);
  const setLoginTokens = useAuthStore((s) => s.setLoginTokens);

  const [
    respondeAuthChallenge,
    {
      loading: respondeAuthLoading,
      data: respondeAuthData,
      error: respondeAuthError,
    },
  ] = useMutation(RESPONDTOAUTHCHALLENGESMS);

  const handleOTP = useCallback(() => {
    respondeAuthChallenge({
      variables: {
        smsMfaCode,
        username,
        clientId: environments.REACT_APP_DEPLOY_USER_POOL_WEB_CLIENT_ID,
        session,
      },
    });
  }, [session, smsMfaCode, username]);

  useEffect(() => {
    if (!respondeAuthData) return;

    if (respondeAuthData?.respondToAuthChallengeSMS?.Error) {
      message.error(respondeAuthData.respondToAuthChallengeSMS.Error.message);
      return;
    }

    const processAuthChallenge = async () => {
      if (respondeAuthData?.respondToAuthChallengeSMS) {
        setLoginTokens({
          jwt: respondeAuthData.respondToAuthChallengeSMS.accessToken.jwt,
          refresh: respondeAuthData.respondToAuthChallengeSMS.refreshToken,
        });

        populateSessionStorage([
          {
            key: ESessionStorageKeys.AUTH_TOKEN,
            value: respondeAuthData.respondToAuthChallengeSMS.accessToken.jwt,
          },
          {
            key: ESessionStorageKeys.REFRESH_TOKEN,
            value: respondeAuthData.respondToAuthChallengeSMS.refreshToken,
          },
        ]);

        if (!isAfterSignUp) {
          const userTypeCheck = await allowedUserType(
            respondeAuthData.respondToAuthChallengeSMS.accessToken.jwt,
            appType
          );

          if (!userTypeCheck) {
            return message.error('Your account type is currently not supported');
          }

          const organisationDetailsObject = await get_all_user_data_v2(username);

          if (!organisationDetailsObject) {
            throw UserException(
              'get_all_user_data - Error encountered getting all user data'
            );
          }

          const allUserData = organisationDetailsObject[0];

          const userData = JSON.stringify({
            token: respondeAuthData.respondToAuthChallengeSMS.accessToken.jwt,
            refresh: respondeAuthData.respondToAuthChallengeSMS.refreshToken,
            sub: username,
            org_id: allUserData.org_id,
          });

          populateSessionStorage([
            {
              key: ESessionStorageKeys.ORG_ID,
              value: allUserData.org_id,
            },
          ]);

          if (username && allUserData?.org_id) {
            mixpanelService.track('v1.Tripplo.SSO.Login.Success', {
              username: username,
              clientId: environments.REACT_APP_DEPLOY_USER_POOL_WEB_CLIENT_ID,
              orgId: allUserData.org_id,
              [EMixPanelMetadataKeys.AUTH_TYPE]: EMixPanelAuthTypes.WEB_LOGIN_MFA,
              [EMixPanelMetadataKeys.DEPLOY_STAGE]: environments.REACT_APP_DEPLOY_STAGE,
            });
          }

          onCompletedCallback(userData);
        }
      } else {
        message.error('Something went wrong... Please try again');
      }
    };

    processAuthChallenge().catch(() => {
      message.error('Something went wrong... Please try again');
    });
  }, [respondeAuthData, isAfterSignUp, variables]);

  useEffect(() => {
    if (!respondeAuthError) return;

    const errorMessage = parseError(respondeAuthError);
    message.error(errorMessage);
  }, [respondeAuthError]);

  return {
    respondeAuthLoading,
    handleOTP,
  };
};
